<template>
  <div
    class="welcome-top-bar"
    :class="{
      'welcome-top-bar--compact': !isWideEffective,
      'welcome-top-bar--docked': isDocked,
    }"
    @mousedown="handleHeaderDrag"
    @touchstart="handleHeaderDrag"
  >
    <div class="top-bar-section top-bar-left">
      <button
        class="icon-button"
        @click="$emit('home')"
        @mousedown.stop
        @touchstart.stop
        title="Home"
      >
        <i class="fas fa-home"></i>
      </button>

      <button
        v-if="isWideEffective"
        class="icon-button"
        @click="$emit('toggle-history')"
        @mousedown.stop
        @touchstart.stop
        title="Chat History"
      >
        <i class="fas fa-history"></i>
      </button>

      <button
        class="icon-button"
        @click="$emit('toggle-nav-sidebar')"
        @mousedown.stop
        @touchstart.stop
        title="Navigation"
      >
        <i class="fas fa-bars"></i>
      </button>

      <div v-if="isWideEffective" class="oliver-mark">
        <span class="oliver-label">Oliver</span>
      </div>
    </div>

    <div
      v-if="isWideEffective"
      class="top-bar-section top-bar-center header-drag-area"
    >
      <span class="datetime-pill">
        <span class="datetime-dot" aria-hidden="true"></span>
        {{ dateLabel }} · {{ timeLabel }}
      </span>
    </div>

    <div v-else class="header-drag-area-spacer"></div>

    <div class="top-bar-section top-bar-right">
      <button
        class="new-chat-button"
        @click="$emit('new-chat')"
        @mousedown.stop
        @touchstart.stop
        title="New Chat"
      >
        <i class="fas fa-plus"></i>
        <span v-if="isWideEffective" class="new-chat-label">New Chat</span>
      </button>

      <button
        v-if="!isWideEffective"
        class="icon-button"
        @click="$emit('toggle-history')"
        @mousedown.stop
        @touchstart.stop
        title="Chat History"
      >
        <i class="fas fa-history"></i>
      </button>

      <button
        class="icon-button"
        @click="handleToggleDarkMode"
        @mousedown.stop
        @touchstart.stop
        :title="darkModeOn ? 'Switch to light mode' : 'Switch to dark mode'"
      >
        <i :class="darkModeOn ? 'fas fa-sun' : 'fas fa-moon'"></i>
      </button>

      <oliver-theme-picker @mousedown.native.stop @touchstart.native.stop />

      <button
        v-if="deviceType !== 'mobile'"
        class="icon-button"
        @click="$emit('toggle-dock')"
        @mousedown.stop
        @touchstart.stop
        :title="isDocked ? 'Undock' : 'Dock to side'"
      >
        <i :class="isDocked ? 'fas fa-window-restore' : 'fas fa-columns'"></i>
      </button>

      <button
        v-if="isCustomized && !isFullscreen"
        class="icon-button"
        :disabled="isDocked"
        @click="$emit('reset-size')"
        @mousedown.stop
        @touchstart.stop
        title="Reset to default size"
      >
        <i class="fas fa-compress-arrows-alt"></i>
      </button>

      <button
        class="icon-button"
        :disabled="isDocked"
        @click="$emit('toggle-fullscreen')"
        @mousedown.stop
        @touchstart.stop
        :title="isFullscreen ? 'Exit Fullscreen' : 'Expand'"
      >
        <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
      </button>

      <button
        class="icon-button"
        @click="$emit('close')"
        @mousedown.stop
        @touchstart.stop
        title="Close"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import headerDraggable from "@/components/OliverBotV2/mixins/headerDraggable.js";
import OliverThemePicker from "@/components/OliverBotV2/theme/ThemePicker.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "WelcomeTopBar",
  components: { OliverThemePicker },
  mixins: [headerDraggable],
  props: {
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    isCustomized: {
      type: Boolean,
      default: false,
    },
    isWide: {
      type: Boolean,
      default: true,
    },
    isDocked: {
      type: Boolean,
      default: false,
    },
    deviceType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      now: new Date(),
      tickHandle: null,
      selfWidth: Infinity,
      isWideEffectiveState: true,
    };
  },
  computed: {
    ...mapState({
      darkModeOn: (state) => state.oliverChatBotV2.darkMode,
    }),
    isWideEffective() {
      return this.isWideEffectiveState;
    },
    dateLabel() {
      return this.now.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    },
    timeLabel() {
      return this.now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    },
  },
  watch: {
    isWide: {
      immediate: true,
      handler() {
        this.updateWideEffective();
      },
    },
    selfWidth() {
      this.updateWideEffective();
    },
  },
  mounted() {
    this.tickHandle = setInterval(() => {
      this.now = new Date();
    }, 30000);
    this._ro = new ResizeObserver((entries) => {
      try {
        // Defer updating reactive state to avoid synchronous resize loop issues
        window.requestAnimationFrame(() => {
          const entry = entries && entries[0];
          if (!entry) return;
          this.selfWidth = entry.contentRect.width;
        });
      } catch (err) {
        // Ignore ResizeObserver loop completed errors in dev overlay
        // Optionally log for debugging: console.debug(err)
      }
    });
    this._ro.observe(this.$el);
  },
  beforeDestroy() {
    if (this.tickHandle) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
  },
  methods: {
    ...mapActions("oliverChatBotV2", ["setDarkMode"]),
    updateWideEffective() {
      if (!this.isWide) {
        this.isWideEffectiveState = false;
        return;
      }

      // Hysteresis prevents layout thrashing near the breakpoint.
      const expandThreshold = 1020;
      const compactThreshold = 1000;

      if (this.isWideEffectiveState) {
        if (this.selfWidth < compactThreshold) {
          this.isWideEffectiveState = false;
        }
        return;
      }

      if (this.selfWidth >= expandThreshold) {
        this.isWideEffectiveState = true;
      }
    },
    handleToggleDarkMode() {
      const next = !this.darkModeOn;
      this.setDarkMode(next);
      this.$emit("toggle-dark-mode", next);
    },
  },
};
</script>

<style lang="scss" scoped>
.welcome-top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  height: 56px;
  background: var(--oliver-header-bg, rgba(255, 255, 255, 0.4));
  position: relative;
  z-index: 10;
  flex-shrink: 0;

  &--compact {
    height: 48px;
    padding: 6px 10px;
    gap: 6px;
  }
}

.top-bar-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-bar-left {
  flex-shrink: 0;
}

.top-bar-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;
  cursor: move;
  touch-action: none;
  pointer-events: auto;
  min-height: 36px;
  z-index: 1;
}

.header-drag-area-spacer {
  flex: 1;
  min-height: 36px;
  cursor: move;
  touch-action: none;
}

.welcome-top-bar--docked {
  .top-bar-center,
  .header-drag-area-spacer {
    cursor: default;
    touch-action: auto;
  }
}

.top-bar-right {
  margin-left: auto;
}

.top-bar-right {
  flex-shrink: 0;
  margin-left: auto;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--oliver-btn-subtle-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-accent-border, rgba(0, 0, 0, 0.06));
  color: var(--oliver-btn-subtle-color);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  position: relative;
  flex-shrink: 0;

  i {
    pointer-events: none;
  }
}

.welcome-top-bar--compact .icon-button {
  width: 32px;
  height: 32px;
  font-size: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.icon-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--oliver-accent);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.4);
}

.oliver-mark {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
}

.oliver-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--oliver-text);
}

.datetime-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 18px;
  background: var(--oliver-btn-subtle-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-accent-border, rgba(0, 0, 0, 0.06));
  font-size: 12.5px;
  font-weight: 500;
  color: var(--oliver-btn-subtle-color);
  white-space: nowrap;
  cursor: auto;
}

.datetime-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--oliver-accent);
}

.new-chat-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 18px;
  background: var(--oliver-btn-subtle-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-accent-border, rgba(0, 0, 0, 0.06));
  color: var(--oliver-btn-subtle-color);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  height: 36px;

  i {
    font-size: 11px;
  }
}

.welcome-top-bar--compact .new-chat-button {
  height: 32px;
  width: 32px;
  padding: 0;
  border-radius: 50%;
  justify-content: center;

  i {
    font-size: 12px;
  }
}

.new-chat-label {
  font-weight: 500;
}
</style>
